import { useEffect, useState } from "react";
import axios from "axios";
import { Layers } from "lucide-react";
import { API } from "../../config";

const TXT = {
  it: {
    title: "Dettaglio della memoria", live: "Processi attivi ora sul server", proc: "Processo", n: "n.", mem: "Memoria",
    total: "Memoria occupata dal servizio", of: "di", comp: "Componenti del servizio applicativo a riposo",
    compNote: (d) => `Misurato il ${d} sull'ambiente di collaudo, con lo stesso codice della produzione, senza utenti collegati.`,
    compTotal: "Totale a riposo",
    ai: (a, d) => `${a.label}: ~ ${a.mb} MB, caricata solo quando viene richiesta una traduzione (dal ${d}). Stato ora: ${a.loaded ? "in memoria" : "non caricata"}.`,
  },
  en: {
    title: "Memory breakdown", live: "Processes running now on the server", proc: "Process", n: "no.", mem: "Memory",
    total: "Memory used by the service", of: "of", comp: "Application service components at rest",
    compNote: (d) => `Measured on ${d} in the staging environment, same code as production, with no users connected.`,
    compTotal: "Total at rest",
    ai: (a, d) => `${a.label}: ~ ${a.mb} MB, loaded only when a translation is requested (since ${d}). Status now: ${a.loaded ? "in memory" : "not loaded"}.`,
  },
};

const Row = ({ label, mb, count, testId }) => (
  <tr className="border-t border-gray-800 text-gray-300" data-testid={testId}>
    <td className="py-2 pr-4">{label}</td>
    {count !== undefined && <td className="py-2 pr-4 text-gray-500">{count}</td>}
    <td className="py-2 text-right font-semibold whitespace-nowrap">~ {mb} MB</td>
  </tr>
);

export const InfraMemoryBreakdown = ({ headers, lang = "it" }) => {
  const t = TXT[lang === "en" ? "en" : "it"];
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/infra-resources/memory-breakdown`, { params: { lang: lang === "en" ? "en" : "it" }, headers })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const d = new Date(data.components_measured_at).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT");
  return (
    <div className="border-t border-gray-800 pt-4 space-y-4" data-testid="infra-memory-breakdown">
      <p className="flex items-center gap-2 text-sm text-white font-medium"><Layers size={15} /> {t.title}</p>
      <div>
        <p className="text-xs text-gray-500 mb-1">{t.live}</p>
        {data.container_used_mb != null && (
          <p className="text-xs text-gray-400 mb-2" data-testid="infra-memory-container">
            {t.total}: <b className="text-white">{data.container_used_mb} MB</b> {t.of} {data.container_limit_mb} MB
          </p>
        )}
        <table className="w-full text-xs">
          <thead><tr className="text-gray-500 text-left"><th className="py-2 pr-4 font-normal">{t.proc}</th><th className="py-2 pr-4 font-normal">{t.n}</th><th className="py-2 text-right font-normal">{t.mem}</th></tr></thead>
          <tbody>{data.processes.map((p) => <Row key={p.key} label={p.label} mb={p.mb} count={p.count} testId={`infra-memory-proc-${p.key}`} />)}</tbody>
        </table>
      </div>
      <div>
        <p className="text-xs text-gray-500 mb-1">{t.comp}</p>
        <table className="w-full text-xs">
          <tbody>
            {data.components.map((c, i) => <Row key={i} label={c.label} mb={c.mb} testId={`infra-memory-comp-${i}`} />)}
            <tr className="border-t border-gray-700 text-white font-semibold" data-testid="infra-memory-comp-total">
              <td className="py-2">{t.compTotal}</td><td className="py-2 text-right">~ {data.components_total_mb} MB</td>
            </tr>
          </tbody>
        </table>
        <p className="text-[11px] text-gray-500 mt-1">{t.compNote(d)}</p>
        {data.ai_library && (
          <p className="text-[11px] text-gray-400 mt-1" data-testid="infra-memory-ai-lib">
            {t.ai(data.ai_library, new Date(data.ai_library.lazy_since).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT"))}
          </p>
        )}
      </div>
    </div>
  );
};
